import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { pageOffset, readPagingQuery, syncPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import EmailThreadDrawer from './EmailThreadDrawer.vue'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_COLORS, emailLabel, emailSourceLabel, eventToStatus } from './emailLogLabels'


export default /*#__PURE__*/_defineComponent({
  __name: 'EmailCustomers',
  emits: ["switch"],
  setup(__props, { emit }: { emit: ({ (e: 'switch', allMessages: boolean): void }), expose: any, slots: any, attrs: any }) {

// Email tab, default view: one row per customer with their latest email and its status
// (get-email-log-for-group?by=customer). Clicking a row opens that customer's emails as a
// thread.

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/email-log`

const loading = ref(true)
const customers = ref<any[]>([])
const totalRows = ref(0)
const currentPage = ref(1)
const perPage = ref(25)
const perPageOptions = [10, 25, 50, 100]
const filterValues = ref<Record<string, any>>({ email: '', search: '', status: [], sent_date: null, every_message: false })
const drawerOpen = ref(false)
const selectedRecipientId = ref<number | null>(null)
const selectedRecipient = ref<any>(null)

const filterSchema = computed(() => [
  { key: 'email', type: 'text', label: 'Customer', placeholder: 'Customer email', operatorLabel: 'contains', primary: true },
  { key: 'search', type: 'text', label: 'Email', placeholder: 'Template or email type', operatorLabel: 'contains', primary: true },
  { key: 'status', type: 'multiselect', label: 'Latest status', primary: true,
    options: Object.keys(EMAIL_STATUS_LABELS).map((value) => ({ label: EMAIL_STATUS_LABELS[value], value })) },
  { key: 'sent_date', type: 'daterange', label: 'Sent date', primary: true },
  { key: 'every_message', type: 'toggle', label: 'Every message', primary: true }
])

const columns = [
  { key: 'customer', label: 'Customer' },
  { key: 'email_name', label: 'Latest email' },
  { key: 'status', label: 'Status' },
  { key: 'timestamp', label: 'Sent' }
]

const hasActiveFilters = computed(() => {
  const f = filterValues.value
  return !!(f.email || f.search || (f.status && f.status.length) || (f.sent_date && (f.sent_date.from || f.sent_date.to)))
})
const canDownload = computed(() => {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
    return Array.isArray(permissions) && permissions.includes('download_data')
  } catch (e) {
    return false
  }
})

function statusLabel(eventType: string) { return EMAIL_STATUS_LABELS[eventToStatus(eventType)] }
function statusColor(eventType: string) { return EMAIL_STATUS_COLORS[eventToStatus(eventType)] }
function needsAttention(row: any) { return row.latest_event === 'Bounce' || row.latest_event === 'Complaint' }
function recipientName(row: any) { return [row.recipient_first_name, row.recipient_last_name].filter(Boolean).join(' ') }
function formatWhen(value: string) {
  if (!value) return ''
  try { return formatGroupDate(value) + ' ' + formatGroupTime(value) } catch (e) { return String(value).slice(0, 16) }
}

function queryString(count: number, offset: number) {
  const params = new URLSearchParams()
  params.append('by', 'customer')
  params.append('count', String(count))
  params.append('offset', String(offset))
  const f = filterValues.value
  if (f.email) params.append('email', f.email)
  if (f.search) params.append('search', f.search)
  if (Array.isArray(f.status) && f.status.length) params.append('status', f.status.join(','))
  if (f.sent_date && f.sent_date.from) params.append('date_from', f.sent_date.from)
  if (f.sent_date && f.sent_date.to) params.append('date_to', f.sent_date.to)
  return params.toString()
}
async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}
async function fetchPage(count: number, offset: number) {
  const config = await authConfig()
  const response = await axios.get(`${baseUrl}?${queryString(count, offset)}`, config)
  return { rows: response.data.customers || [], total: response.data.total_rows || 0 }
}
async function loadCustomers() {
  loading.value = true
  try {
    const page = await fetchPage(perPage.value, pageOffset(currentPage.value, perPage.value))
    customers.value = page.rows
    totalRows.value = page.total
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load emails. Please refresh the page.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

// The first change after mount is FilterBar's restore (last-used or default view). The
// last-used save is debounced, so a restore can still carry the other view's toggle state
// for a moment; it never switches views. Only an explicitly opened saved view (?view= on
// mount) or a later user change does.
const presetOnMount = !!proxy.$route.query.view
let restored = false
const filterBarEl = ref<any>(null)
function settleView(values: Record<string, any>, here: boolean): boolean {
  const first = !restored
  restored = true
  if ((values.every_message === true) === here) return true
  if (first && !presetOnMount) {
    filterValues.value.every_message = here
    if (filterBarEl.value) filterBarEl.value.applyValues({ ...values, every_message: here })
    return true
  }
  emit('switch', !here)
  return false
}
function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!settleView(filterValues.value, false)) return
  currentPage.value = 1
  loadCustomers()
}
function onPerPageChange(n: number, meta?: { restoring?: boolean }) {
  perPage.value = n
  currentPage.value = 1
  if (meta && meta.restoring) return
  loadCustomers()
}
function onPageChange(page: number) {
  currentPage.value = page
  loadCustomers()
}
function openCustomer(row: any) {
  selectedRecipientId.value = row.recipient_id
  selectedRecipient.value = row
  drawerOpen.value = true
}

async function downloadCsvExport() {
  loading.value = true
  try {
    const rows = await fetchAllPages(fetchPage, 1000)
    downloadCsv('email_customers.csv', [
      { label: 'Customer', value: (row: any) => recipientName(row) },
      { label: 'Email address', value: (row: any) => row.recipient_email || '' },
      { label: 'Emails sent', value: (row: any) => row.email_count || 0 },
      { label: 'Latest email', value: (row: any) => emailLabel(row) },
      { label: 'Latest status', value: (row: any) => statusLabel(row.latest_event) },
      { label: 'Sent', value: (row: any) => formatGroupDate(row.timestamp, { dayOfWeek: false }) + ' ' + formatGroupTime(row.timestamp) }
    ], rows)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

watch(() => proxy.$route.query.customer, (value) => {
  if (value && !drawerOpen.value) {
    selectedRecipientId.value = Number(value)
    selectedRecipient.value = null
    drawerOpen.value = true
  }
})

onMounted(() => {
  const paging = readPagingQuery(proxy.$route, 25)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 25
  currentPage.value = paging.page
  loadCustomers()
})

return { __sfc: true,emit, proxy, companyId, companyGroupId, baseUrl, loading, customers, totalRows, currentPage, perPage, perPageOptions, filterValues, drawerOpen, selectedRecipientId, selectedRecipient, filterSchema, columns, hasActiveFilters, canDownload, statusLabel, statusColor, needsAttention, recipientName, formatWhen, queryString, authConfig, fetchPage, loadCustomers, presetOnMount, restored, filterBarEl, settleView, onFiltersChanged, onPerPageChange, onPageChange, openCustomer, downloadCsvExport, tagChipStyle, EmailThreadDrawer, emailLabel, emailSourceLabel }
}

})